import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import type { z } from 'zod'
import { kPostSignupPhoneCompletePath, kPostSignupPhoneStartPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { CompletePostSignupPhoneResponseSchema, StartPostSignupPhoneResponseSchema } from '@/onboarding/schema/post-signup-phone.schema'
import type {
  CompletePostSignupPhoneRequest,
  CompletePostSignupPhoneResponse,
  StartPostSignupPhoneResponse,
} from '@/onboarding/schema/post-signup-phone.schema'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

type PostSignupPhoneRequest<TResponse> = {
  body?: CompletePostSignupPhoneRequest
  operation: 'start' | 'complete'
  path: string
  responseSchema: z.ZodType<TResponse>
}

function formatErrorDetail(data: unknown): string {
  const responseData = data as { detail?: unknown } | undefined
  const detail = responseData?.detail ?? data
  if (typeof detail === 'string') return detail
  if (detail == null) return ''
  return JSON.stringify(detail)
}

async function postSignupPhoneRequest<TResponse>({ body, operation, path, responseSchema }: PostSignupPhoneRequest<TResponse>): Promise<TResponse> {
  const transactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': transactionId,
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      ...(body ? { body: JSON.stringify(body) } : {}),
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${path}`
    const response = await fetch(apiUrl, requestOptions)
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatErrorDetail(errorData)
      const invalidPhone = operation === 'complete' && response.status === 422
      if (!invalidPhone) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: transactionId })
          Sentry.captureException(new Error(`Post-signup phone ${operation} request failed`), {
            extra: {
              ...getResponseDebugContext(response, apiUrl),
              responseData: errorData,
              parseError: parseError ? formatUnknownError(parseError) : null,
              onLine: navigator.onLine,
              cookieEnabled: navigator.cookieEnabled,
            },
          })
        })
      }
      throw new ApiError(response.status, `Post-signup phone ${operation} request failed`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(new Error(`Post-signup phone ${operation} response JSON parse failed`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            parseError: formatUnknownError(parseError),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, `Invalid post-signup phone ${operation} response JSON`)
    }

    const parsed = responseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(new Error(`Post-signup phone ${operation} response schema validation failed`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, `Invalid post-signup phone ${operation} response shape`, parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(error, {
          extra: { operation, onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}

export function postStartPostSignupPhone(): Promise<StartPostSignupPhoneResponse> {
  return postSignupPhoneRequest({
    operation: 'start',
    path: kPostSignupPhoneStartPath,
    responseSchema: StartPostSignupPhoneResponseSchema,
  })
}

export function postCompletePostSignupPhone(request: CompletePostSignupPhoneRequest): Promise<CompletePostSignupPhoneResponse> {
  return postSignupPhoneRequest({
    body: request,
    operation: 'complete',
    path: kPostSignupPhoneCompletePath,
    responseSchema: CompletePostSignupPhoneResponseSchema,
  })
}
